import { get, omit, set, toPath } from 'lodash-es';
import { restApi } from '@icp/settings';
import { getLanguage, getTFunc } from './i18nForBrowser';

/**
 * 使用当前语言从props翻译指定字段k
 * 优先使用译文字段, 格式`${k}_i18n_${lng}`, 比如: title的中文译文字段 title_i18n_zh-CN: "标题"
 * 否则使用i18n key字段，格式`${k}_i18n_key` 和可选的namespace `${k}_i18n_ns`, 比如: title_i18n_key: "myForm.myField.title": "标题"
 * 否则使用字段k的值
 *
 * 此方法不会触发加载 `${k}_i18n_ns` 语言包, 请确保使用的语言包已被加载完毕
 *
 * @param k
 * @param props
 * @param t
 * @param lng
 * @returns {*|undefined}
 */
export function translate(k, props, t = getTFunc(), lng = getLanguage()) {
  const untranslatedText = get(props, k);
  const translatedText = get(props, `${k}_i18n_${lng}`);
  const i18nKey = get(props, `${k}_i18n_key`);
  const i18nNamespace = get(props, `${k}_i18n_ns`);

  if (untranslatedText == null && translatedText == null && i18nKey == null) {
    return undefined;
  }

  return (
    translatedText ??
    (i18nKey
      ? t(i18nKey, { ns: i18nNamespace, defaultValue: untranslatedText }) ?? untranslatedText
      : //
        untranslatedText)
  );
}

function findKeyToTranslate(obj, kp, cb, skip, objMap = new WeakMap()) {
  if (!obj) return;
  // 识别循环引用退出递归
  if (objMap.has(obj)) return;
  objMap.set(obj, true);

  for (const [k, v] of Object.entries(obj)) {
    if (skip?.(k, v, obj, kp)) continue;
    if (k.startsWith('_')) {
      // ignore internal fields
    } else if (Array.isArray(v)) {
      v.filter((subO) => typeof subO === 'object').forEach((subO, i) => {
        findKeyToTranslate(subO, kp ? `${kp}.${k}[${i}]` : `${k}[${i}]`, cb, skip, objMap);
      });
    } else if (typeof v === 'object') {
      findKeyToTranslate(v, kp ? `${kp}.${k}` : k, cb, skip, objMap);
    } else if (k.includes('_i18n_')) {
      const keyToTranslate = k.replace(/_i18n_.*$/, '');
      cb(kp ? `${kp}.${keyToTranslate}` : keyToTranslate);
    }
  }
}

// This method mutates props
function deleteTranslationProps(props, keyPath) {
  const lastKey = keyPath[keyPath.length - 1];
  const keyPathPre = keyPath.slice(0, keyPath.length - 1);

  const obj = keyPathPre.length ? get(props, keyPathPre) : props;

  if (Object.isSealed(obj)) return;

  Object.keys(obj || {})
    .filter((k) => k.startsWith(`${lastKey}_i18n_`))
    .forEach((k) => {
      delete obj[k];
    });
}

const hasTranslatedSymbol = Symbol('hasTranslatedSymbol');

function markTranslated(obj) {
  if (Object.isExtensible(obj)) {
    obj[hasTranslatedSymbol] = true;
  }
}

const TRANSLATE_ENTIRE_OBJ_DEFAULT_OPTIONS = () => ({
  t: getTFunc(),
  lng: getLanguage(),
  skip: undefined,
  deleteI18nVariantProps: false,
  // 无视hasTranslatedSymbol强制翻译
  force: false,
});

/**
 * 此方法修改传入的obj
 * 深度遍历obj翻译所有标记_i18n_的字段
 * _i18n_配置字段将被删除
 */
export function translateEntireObj(obj, options) {
  const { t, lng, skip, deleteI18nVariantProps, force } = {
    ...TRANSLATE_ENTIRE_OBJ_DEFAULT_OPTIONS(),
    ...options,
  };

  if (Array.isArray(obj)) {
    for (const item of obj) {
      translateEntireObj(item, options);
    }
    markTranslated(obj);
    return;
  }

  if (!obj) return;
  if (typeof obj !== 'object') return;
  if (obj.skipTranslation) return;
  if (!force && obj[hasTranslatedSymbol]) return;
  // console.time('translateEntireObj');
  const kpToTranslateSet = new Set();

  findKeyToTranslate(
    obj,
    null,
    (kp) => {
      kpToTranslateSet.add(kp);
    },
    skip,
  );

  for (const k of kpToTranslateSet) {
    const translated = translate(k, obj, t, lng);

    if (translated != null) {
      const kp = toPath(k);
      if (get(obj, kp) !== translated) {
        try {
          const parent = kp.length > 1 ? get(obj, kp.slice(0, kp.length - 1)) : obj;
          if (!Object.isFrozen(parent)) {
            set(obj, kp, translated);
          }
        } catch (e) {
          console.error(e);
        }
      }
      if (deleteI18nVariantProps) {
        deleteTranslationProps(obj, kp);
      }
    }
  }

  markTranslated(obj);
  // console.timeEnd('translateEntireObj');
}

export const CUSTOM_I18N_KEY_SEPARATOR = '|>';

// key中不能使用 .
export function escapeI18nKey(originalKey) {
  return originalKey.replace(/\./g, CUSTOM_I18N_KEY_SEPARATOR);
}

export function escapeI18nKeyForEntireObj(obj) {
  if (typeof obj !== 'object' || obj == null) return obj;
  if (Array.isArray(obj)) throw Error('Unexpected data type array');
  return Object.fromEntries(
    Object.entries(obj || {}).map(([k, v]) => [escapeI18nKey(k), escapeI18nKeyForEntireObj(v)]),
  );
}

// convert from { "a.b.c": "xxx" } to { "a": { "b": { "c": "xxx" } } }
export function convertI18nResourceFormat(obj) {
  const result = {};
  for (const [k, v] of Object.entries(obj)) {
    set(result, k.split('.'), v);
  }
  return result;
}

export function localeCompare(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 0;
  try {
    return a.localeCompare(b, getLanguage());
  } catch {
    return a.localeCompare(b);
  }
}

export function findI18nValues(data, path) {
  if (typeof path !== 'string') {
    return {};
  }

  const arrayPath = toPath(path);
  const parentPath = arrayPath.slice(0, -1);
  const propertyKey = arrayPath[arrayPath.length - 1];
  const parentObj = parentPath.length ? get(data, parentPath) : data;

  const i18nValues = {};

  // 如果未配置相关的i18n资源 则直接返回
  if (!parentObj || typeof parentObj !== 'object') return i18nValues;

  for (const [k, v] of Object.entries(parentObj)) {
    if (k.startsWith(`${propertyKey}_i18n_`)) {
      const suffix = k.split('_i18n_')[1];
      i18nValues[`_i18n_${suffix}`] = v;
    }
  }

  return i18nValues;
}

// 提取整个项目i18n key
export function extractI18nKeysFromProject(projectId, signal) {
  return (
    Promise.all([
      restApi
        .get(`/form/api/project/seed-data/export-with-json-file/${projectId}`, { signal })
        .then((projectSeedData) => {
          return [
            // appConfig & routers
            JSON.parse(projectSeedData?.project?.frontEndConfig?.config || null),
            // pbc (info & routes)
            projectSeedData?.project?.pbcList?.map((pbc) => [
              omit(pbc, ['formEntityList', 'formEntityPageList', 'flowDefinitionList']),
              JSON.parse(pbc.routesJson || null),
              // page
              pbc.formEntityPageList?.map((page) => JSON.parse(page.schemaJson || null)),
              // form
              pbc.formEntityList
                ?.flatMap((form) => form.layouts || [])
                .map((layout) => JSON.parse(layout.schemaJson || null)),
            ]),
          ];
        }),
      // role
      restApi
        .get(`/user-management/api/role/platform-list`, {
          signal,
          params: {
            payload: JSON.stringify({
              filterModel: [
                { colId: 'projectId', filterType: 'text', type: 'equals', filter: `${projectId}` },
              ],
            }),
          },
        })
        .then((res) => res.results || []),
      // permission
      restApi.get(`/user-management/api/permission/list-by-project-id/${projectId}`, { signal }),
    ])
      // 提取
      .then(extractI18nKeys)
  );
}

export function extractI18nKeys(object) {
  const keys = [];

  function traverse(obj) {
    if (!obj) return;
    if (typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
      obj.forEach(traverse);
      return;
    }

    for (const [key, value] of Object.entries(obj)) {
      if (key.endsWith('_i18n_key') && typeof value === 'string') {
        if (!value) continue;
        const namespace = obj[key.replace('_i18n_key', '_i18n_ns')] || 'default';
        const defaultValue = obj[key.replace('_i18n_key', '')];
        keys.push({
          key: value,
          namespace,
          defaultValue,
        });
      } else if (typeof value === 'object') {
        traverse(value);
      }
    }
  }

  traverse(object);

  return keys;
}
